import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(form.email, form.password, form.name);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created");
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100 p-6" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 rounded-md bg-white text-black flex items-center justify-center">
            <Activity className="w-4 h-4" />
          </div>
          <div className="text-sm font-semibold">Digi-One</div>
        </div>
        <div className="label-overline mb-2">Create account</div>
        <h2 className="text-2xl font-semibold mb-1 tracking-tight" style={{ fontFamily: "Outfit" }}>
          Join the console
        </h2>
        <p className="text-sm text-zinc-500 mb-8">Set up your workspace in seconds.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs text-zinc-400">Full name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Doe"
              className="bg-zinc-900 border-white/10 text-white"
              required
              data-testid="register-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs text-zinc-400">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@company.com"
              className="bg-zinc-900 border-white/10 text-white"
              required
              data-testid="register-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs text-zinc-400">Password</Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 6 characters"
              className="bg-zinc-900 border-white/10 text-white"
              required
              data-testid="register-password-input"
            />
          </div>
          {error && (
            <div className="text-sm text-red-400" data-testid="register-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black hover:bg-zinc-200 font-medium"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
        </form>
        <div className="text-xs text-zinc-500 mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-white hover:underline" data-testid="register-go-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
